import { z } from "zod";

import { matchAndClubCountryCodes } from "./countries";
import { clubHosting, clubTraveling, playerFFTranking, playerPreferredSurface, rankingTypes, tournamentPrizesCategorie } from "./enums";
import { ATP_WTA_ITF_Regex, UTRRegex } from "./player";
import { ATP_WTA_ITF_ErrorMsg, dateMsg, minLengthMsg, priceMsg, requiredMsg, UTR_ErrorMsg } from "./zod-error-msg";

export const tournamentInformationsSchema = z.object({
  name: z.string().min(2, { message: minLengthMsg }),
  city: z.string().min(2, { message: minLengthMsg }),
  country: z.enum(matchAndClubCountryCodes, { message: requiredMsg }),
  description: z.string().max(750, { message: "common:zod.description" }).optional(),
  startDate: z.string({ message: requiredMsg }).date(dateMsg),
  endDate: z.string({ message: requiredMsg }).date(dateMsg),
  imgUrl: z.string().url(),
});
export type TournamentInformationsSchema = z.infer<typeof tournamentInformationsSchema>;

export const tournamentPlayersSchema = z.object({
  id: z.string().optional(),
  rankingType: z.enum(rankingTypes, { message: requiredMsg }),
  ranking: z.string({ message: requiredMsg }),
  rankingNumber: z.coerce.number({ message: requiredMsg }).positive({ message: requiredMsg }).optional(),
  number: z.coerce.number({ message: requiredMsg }).positive({ message: requiredMsg }),
}).superRefine((data, ctx) => {
  if (data.ranking === "T100" && data.rankingNumber === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["rankingNumber"],
      message: requiredMsg,
    });
  }
  if (data.rankingType === "FFT") {
    if (!playerFFTranking.includes(data.ranking as any)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranking"],
        message: requiredMsg,
      });
    }
  }
  else if (data.rankingType === "UTR") {
    if (!UTRRegex.test(data.ranking)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranking"],
        message: UTR_ErrorMsg,
      });
    }
  }
  else if (["ATP", "WTA", "ITF"].includes(data.rankingType)) {
    if (!ATP_WTA_ITF_Regex.test(data.ranking)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranking"],
        message: ATP_WTA_ITF_ErrorMsg,
      });
    }
  }
});
export type TournamentPlayersSchema = z.infer<typeof tournamentPlayersSchema>;

export const tournamentPlayersSchemaDelete = z.object({
  id: z.string().uuid(),
});

export const tournamentPrizesSchema = z.object({
  prizeCategorieToUpdate: z.enum(tournamentPrizesCategorie),
  prize: z.literal("").transform(() => null).or(z.coerce.number().positive({ message: priceMsg })).nullable(),
});
export type TournamentPrizesSchema = z.infer<typeof tournamentPrizesSchema>;

export const tournamentSurfaceSchema = z.object({ tournamentSurface: z.enum(playerPreferredSurface, { message: requiredMsg }) });
export type TournamentSurfaceSchema = z.infer<typeof tournamentSurfaceSchema>;

export const tournamentHostingSchema = z.object({ tournamentHosting: z.enum(clubHosting, { message: requiredMsg }) });
export type TournamentHostingSchema = z.infer<typeof tournamentHostingSchema>;

export const tournamentTravelingSchema = z.object({ tournamentTraveling: z.enum(clubTraveling, { message: requiredMsg }) });
export type TournamentTravelingSchema = z.infer<typeof tournamentTravelingSchema>;

export const tournamentSpecsSchema = tournamentSurfaceSchema.merge(tournamentHostingSchema).merge(tournamentTravelingSchema).partial();
export type TournamentSpecsSchema = z.infer<typeof tournamentSpecsSchema>;
